import { useMemo, useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight } from 'lucide-react'
import { PageContainer } from '@/components/common/PageContainer'
import { PageEmpty, PageError, PageLoading } from '@/components/common/PageStates'
import { RfidAppHeader } from '@/components/rfid/RfidAppHeader'
import { TransferContextCard } from '@/components/rfid/TransferContextCard'
import { BRAND } from '@/constants/brand'
import { getMovement, type MovementId } from '@/config/movements'
import { rfidService } from '@/services/rfid.service'
import type { TransferCustomerRow } from '@/utils/transferCustomers'
import { movementFlowLabel } from '@/utils/movementFlow'

export default function TransferToCustomerPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const movementParam = params.get('movement')
  const fromBpCode = params.get('fromBpCode') ?? ''
  const fromCustomerName = params.get('fromCustomerName') ?? ''
  const [search, setSearch] = useState('')

  const valid =
    (movementParam === 'transfer_out' || movementParam === 'transfer_in') &&
    Boolean(fromBpCode)

  const movement = valid ? getMovement(movementParam as MovementId) : null

  const customersQ = useQuery({
    queryKey: ['transfer-to-customers', fromBpCode],
    queryFn: () => rfidService.fetchTransferToCustomers(fromBpCode),
    enabled: Boolean(fromBpCode) && valid,
  })

  const rows = customersQ.data ?? []

  const filtered = useMemo(() => {
    if (!search.trim()) return rows
    const q = search.trim().toLowerCase()
    return rows.filter(
      (row) =>
        row.customerName.toLowerCase().includes(q) || row.bpCode.toLowerCase().includes(q)
    )
  }, [rows, search])

  if (!valid || !movement) {
    return <Navigate to="/transfer/customer?movement=transfer_out" replace />
  }

  const onSelect = (row: TransferCustomerRow) => {
    navigate(
      `/transfer/items?movement=${movement.id}` +
        `&bpCode=${encodeURIComponent(fromBpCode)}` +
        `&customerName=${encodeURIComponent(fromCustomerName)}` +
        `&toBpCode=${encodeURIComponent(row.bpCode)}` +
        `&toCustomerName=${encodeURIComponent(row.customerName)}`
    )
  }

  return (
    <PageContainer fluid className="!px-0 !py-0" stack={false}>
      <div className="rfid-web-frame">
        <RfidAppHeader
          title={movement.title}
          subtitle={`${movementFlowLabel(movement)} · Select to-customer`}
          onBack={() => navigate(`/transfer/customer?movement=${movement.id}`)}
        />

        <div className="rfid-wizard-body overflow-y-auto">
          <TransferContextCard
            fromLabel={fromCustomerName || fromBpCode}
            fromCode={fromBpCode}
            className="mb-3"
          />

          {customersQ.isLoading ? (
            <PageLoading tip="Loading destination customers…" />
          ) : customersQ.isError ? (
            <PageError
              message="Failed to load to-customers"
              description={(customersQ.error as Error)?.message}
            />
          ) : (
            <>
              <input
                type="search"
                className="rfid-search"
                placeholder="Search…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search destination customers"
              />

              {filtered.length === 0 ? (
                <PageEmpty description="No destination customers found." />
              ) : (
                <div className="rfid-doc-list">
                {filtered.map((row) => (
                  <button
                    key={row.bpCode}
                    type="button"
                    onClick={() => onSelect(row)}
                    className="rfid-doc-row"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block text-[14px] font-extrabold text-foreground">
                        {row.customerName}
                      </span>
                      <span className="mt-1 block text-[11px] font-semibold text-muted-foreground">
                        {row.bpCode}
                      </span>
                    </span>
                    <ChevronRight size={16} color={BRAND} className="shrink-0" />
                  </button>
                ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </PageContainer>
  )
}
